/**
 * activeJobsSync — fetches /api/v1/jobs/active and seeds the BackgroundJobsBar
 * with in-flight and recently-completed jobs.
 *
 * Call on:
 *  - Login (AuthContext / ProtectedRoute first mount)
 *  - App return to foreground (visibilitychange: visible)
 *  - WebSocket reconnect (notification:connected)
 */

import { apiClient } from "../http/apiClient";

export type ActiveJob = {
  job_id: string;
  job_type: "deal_analysis" | "flyer" | "itinerary" | "video" | "ai_media" | string;
  status: "queued" | "started" | "running" | "completed" | "failed";
  stage: string;
  progress: number;
  title: string;
  deal_id: string | null;
  session_id: string | null;
  asset_type: string | null;
  template_name: string | null;
  result_url: string | null;
  error: string | null;
  started_at: string | null;
  updated_at: string | null;
  /** Friendly explanation set by the server when a stuck job is auto-reset */
  user_message?: string | null;
  /** True when a stale upload's disposable draft was removed during reaping */
  staging_deal_deleted?: boolean;
};

export type ActiveJobsResponse = { jobs: ActiveJob[] };

export async function fetchActiveJobs(): Promise<ActiveJob[]> {
  try {
    const { data } = await apiClient.get<ActiveJobsResponse>("/api/v1/jobs/active");
    return data.jobs ?? [];
  } catch {
    return [];
  }
}

/**
 * Authoritative status of a single deal-analysis job, fetched by job_id.
 *
 * Unlike /api/v1/jobs/active (which only returns in-flight jobs + a 5-minute
 * completion window), this works regardless of how long ago the job finished —
 * so we can reconcile a locally-stuck "running" entry whose completion event was
 * missed (WS drop / tab backgrounded) long after it aged out of the active feed.
 *
 * Returns null on any error (job not found, network) so callers leave the local
 * entry untouched rather than wrongly clearing it.
 */
export async function fetchAnalysisJobStatus(
  jobId: string,
): Promise<{ status: string; stage?: string } | null> {
  if (!jobId) return null;
  try {
    const { data } = await apiClient.get<{ status?: string; stage?: string }>(
      `/api/deals/analysis-jobs/${jobId}`,
    );
    if (!data?.status) return null;
    return { status: data.status, stage: data.stage };
  } catch (err: unknown) {
    // 404 means the job no longer exists — treat as failed so the local entry is dismissed.
    // Other errors (network, 5xx) return null so callers leave the entry untouched.
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return { status: "failed" };
    return null;
  }
}
